import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api";
import { useSubscription } from "../SubscriptionContext";
import VoiceInput from "../components/VoiceInput";

const TEMPLATES = [
  // Business & Strategy
  {
    id: "business-plan", label: "Business Plan", icon: "📋", category: "Strategy",
    description: "Comprehensive business plan with market analysis and projections",
    fields: [
      { key: "website", label: "Company Website (optional)", type: "url", placeholder: "https://yourcompany.com" },
      { key: "company", label: "Company/Business Name", type: "text", placeholder: "e.g., TechStart AI Solutions", required: true },
      { key: "industry", label: "Industry", type: "text", placeholder: "e.g., SaaS, E-commerce, Healthcare" },
      { key: "stage", label: "Stage", type: "select", options: ["Idea", "Pre-seed", "Seed", "Series A", "Growth"] },
    ],
    buildPrompt: (f) => `Create a comprehensive business plan for ${f.company || "the company"}${f.website ? ` (website: ${f.website})` : ""}. Industry: ${f.industry || "technology"}. Stage: ${f.stage || "early stage"}. Include executive summary, market analysis, revenue model, competitive landscape, and 3-year financial projections.`
  },
  {
    id: "pitch-deck", label: "Pitch Deck Script", icon: "🎯", category: "Strategy",
    description: "10-slide investor pitch deck with all key sections",
    fields: [
      { key: "website", label: "Company Website", type: "url", placeholder: "https://yourcompany.com" },
      { key: "company", label: "Company Name", type: "text", placeholder: "e.g., TechStart", required: true },
      { key: "problem", label: "Problem You Solve", type: "textarea", placeholder: "What pain point do you address?" },
      { key: "funding", label: "Funding Ask", type: "text", placeholder: "e.g., $500K, $2M" },
    ],
    buildPrompt: (f) => `Write a compelling 10-slide investor pitch deck script for ${f.company}${f.website ? ` (${f.website})` : ""}. Problem: ${f.problem || "to be analyzed from website"}. Funding ask: ${f.funding || "seed round"}. Include: problem, solution, market size ($TAM/$SAM/$SOM), business model, traction, competition, team, financials, and the ask.`
  },
  {
    id: "competitor-analysis", label: "Competitor Analysis", icon: "🔍", category: "Strategy",
    description: "SWOT analysis and competitive landscape report",
    fields: [
      { key: "company", label: "Your Company", type: "text", placeholder: "Your company name", required: true },
      { key: "competitors", label: "Competitors (comma separated)", type: "textarea", placeholder: "e.g., Competitor A, Competitor B, Competitor C" },
      { key: "industry", label: "Industry/Market", type: "text", placeholder: "e.g., Food delivery, CRM software" },
    ],
    buildPrompt: (f) => `Create a detailed SWOT analysis and competitive landscape report for ${f.company} in the ${f.industry || "market"}. Analyze these competitors: ${f.competitors || "top 3-5 competitors in the space"}. Include pricing comparison, feature matrix, market positioning, and strategic recommendations.`
  },
  {
    id: "market-research", label: "Market Research", icon: "📊", category: "Strategy",
    description: "TAM/SAM/SOM analysis with customer segments",
    fields: [
      { key: "market", label: "Market/Industry", type: "text", placeholder: "e.g., AI-powered customer service", required: true },
      { key: "region", label: "Geographic Focus", type: "text", placeholder: "e.g., Pakistan, South Asia, Global" },
      { key: "segments", label: "Target Segments", type: "textarea", placeholder: "e.g., SMBs, Enterprise, Startups" },
    ],
    buildPrompt: (f) => `Conduct comprehensive market research for the ${f.market} market${f.region ? ` in ${f.region}` : ""}. Target segments: ${f.segments || "all segments"}. Include TAM/SAM/SOM analysis with specific numbers, customer segments, buyer personas, growth trends, and market opportunities.`
  },

  // Marketing & Sales
  {
    id: "marketing-plan", label: "Marketing Strategy", icon: "📈", category: "Marketing",
    description: "90-day integrated marketing plan with budget",
    fields: [
      { key: "website", label: "Website/Product URL", type: "url", placeholder: "https://yourproduct.com" },
      { key: "company", label: "Company/Product", type: "text", placeholder: "Company or product name", required: true },
      { key: "budget", label: "Monthly Budget", type: "text", placeholder: "e.g., $5,000, $50,000" },
      { key: "goals", label: "Primary Goals", type: "textarea", placeholder: "e.g., Increase signups by 50%, Generate 100 leads/month" },
    ],
    buildPrompt: (f) => `Create a 90-day integrated marketing strategy for ${f.company}${f.website ? ` (${f.website})` : ""}. Budget: ${f.budget || "flexible"}. Goals: ${f.goals || "growth and brand awareness"}. Include channel strategy, budget allocation, KPIs, campaign calendar, and expected ROI.`
  },
  {
    id: "email-sequence", label: "Email Campaign", icon: "✉️", category: "Marketing",
    description: "7-email nurture sequence with subject lines",
    fields: [
      { key: "product", label: "Product/Service", type: "text", placeholder: "What are you selling?", required: true },
      { key: "audience", label: "Target Audience", type: "text", placeholder: "e.g., SaaS founders, HR managers" },
      { key: "goal", label: "Campaign Goal", type: "select", options: ["Onboarding", "Sales conversion", "Re-engagement", "Upsell", "Educational"] },
    ],
    buildPrompt: (f) => `Write a high-converting 7-email ${f.goal || "nurture"} sequence for ${f.product}. Target audience: ${f.audience || "potential customers"}. Include compelling subject lines, preview text, email body copy, and clear CTAs for each email. Focus on building trust and driving action.`
  },
  {
    id: "social-calendar", label: "Content Calendar", icon: "📅", category: "Marketing",
    description: "4-week social media content with post copy",
    fields: [
      { key: "brand", label: "Brand/Company", type: "text", placeholder: "Your brand name", required: true },
      { key: "platforms", label: "Platforms", type: "text", placeholder: "e.g., Instagram, LinkedIn, Twitter" },
      { key: "tone", label: "Brand Voice", type: "select", options: ["Professional", "Casual & Fun", "Educational", "Inspirational", "Bold & Edgy"] },
      { key: "topics", label: "Key Topics/Themes", type: "textarea", placeholder: "e.g., Product tips, Industry news, Behind the scenes" },
    ],
    buildPrompt: (f) => `Create a 4-week social media content calendar for ${f.brand} on ${f.platforms || "Instagram, LinkedIn, Twitter"}. Brand voice: ${f.tone || "professional"}. Topics: ${f.topics || "variety of engaging content"}. Include ready-to-post copy, hashtags, optimal posting times, and engagement hooks.`
  },
  {
    id: "seo-strategy", label: "SEO Strategy", icon: "🔎", category: "Marketing",
    description: "Keyword research and content pillars",
    fields: [
      { key: "website", label: "Website URL", type: "url", placeholder: "https://yoursite.com", required: true },
      { key: "keywords", label: "Target Keywords (if known)", type: "textarea", placeholder: "e.g., best CRM software, sales automation" },
      { key: "competitors", label: "SEO Competitors", type: "text", placeholder: "e.g., hubspot.com, salesforce.com" },
    ],
    buildPrompt: (f) => `Develop a comprehensive SEO strategy for ${f.website}. Target keywords: ${f.keywords || "to be researched"}. Competitors: ${f.competitors || "top ranking sites"}. Include keyword research with search volume estimates, content pillars, on-page optimization checklist, and technical SEO recommendations.`
  },

  // Operations & HR
  {
    id: "proposal", label: "Client Proposal", icon: "📄", category: "Operations",
    description: "Professional proposal with scope and pricing",
    fields: [
      { key: "your_company", label: "Your Company Name", type: "text", placeholder: "Your company/agency name", required: true },
      { key: "your_website", label: "Your Website", type: "url", placeholder: "https://yourcompany.com" },
      { key: "your_expertise", label: "Your Expertise & USP", type: "textarea", placeholder: "e.g., 10+ years in web development, served 100+ clients, specialized in e-commerce" },
      { key: "client", label: "Client Name", type: "text", placeholder: "Client company name", required: true },
      { key: "project", label: "Project/Service", type: "text", placeholder: "e.g., Website redesign, App development", required: true },
      { key: "budget", label: "Budget Range", type: "text", placeholder: "e.g., $10,000 - $25,000" },
      { key: "timeline", label: "Timeline", type: "text", placeholder: "e.g., 8 weeks, 3 months" },
    ],
    buildPrompt: (f) => `Write a professional client proposal from ${f.your_company}${f.your_website ? ` (${f.your_website})` : ""} to ${f.client} for ${f.project}. Our expertise: ${f.your_expertise || "professional services provider"}. Budget: ${f.budget || "to be discussed"}. Timeline: ${f.timeline || "to be determined"}. Include: compelling introduction about our company and why we're the best fit, executive summary, understanding of client needs, proposed solution, scope of work, deliverables, timeline with milestones, pricing breakdown with value justification, our relevant experience and case studies, terms & conditions, and strong call to action.`
  },
  {
    id: "meeting-agenda", label: "Meeting Agenda", icon: "🤝", category: "Operations",
    description: "Structured agenda with objectives and action items",
    fields: [
      { key: "meeting", label: "Meeting Type", type: "select", options: ["Board Meeting", "Team Standup", "Client Kickoff", "Quarterly Review", "Strategy Session", "1:1 Review"], required: true },
      { key: "duration", label: "Duration", type: "select", options: ["30 minutes", "1 hour", "2 hours", "Half day"] },
      { key: "topics", label: "Key Topics", type: "textarea", placeholder: "What needs to be discussed?" },
    ],
    buildPrompt: (f) => `Create a structured agenda for a ${f.meeting}. Duration: ${f.duration || "1 hour"}. Topics to cover: ${f.topics || "standard agenda items"}. Include objectives, timed discussion points, decision items, action item slots, and follow-up section.`
  },
  {
    id: "sop-document", label: "SOP Document", icon: "📑", category: "Operations",
    description: "Step-by-step procedure with quality checks",
    fields: [
      { key: "process", label: "Process Name", type: "text", placeholder: "e.g., Customer Onboarding, Order Fulfillment", required: true },
      { key: "department", label: "Department", type: "text", placeholder: "e.g., Sales, Operations, Support" },
      { key: "tools", label: "Tools/Systems Used", type: "text", placeholder: "e.g., Salesforce, Slack, Notion" },
    ],
    buildPrompt: (f) => `Create a detailed Standard Operating Procedure (SOP) for ${f.process} in the ${f.department || "operations"} department. Tools: ${f.tools || "standard tools"}. Include purpose, scope, roles & responsibilities, step-by-step instructions, quality checks, exceptions handling, and revision history template.`
  },
  {
    id: "job-description", label: "Job Description", icon: "👤", category: "Operations",
    description: "Compelling JD with responsibilities and requirements",
    fields: [
      { key: "title", label: "Job Title", type: "text", placeholder: "e.g., Senior Product Manager", required: true },
      { key: "company", label: "Company", type: "text", placeholder: "Your company name" },
      { key: "level", label: "Level", type: "select", options: ["Entry Level", "Mid Level", "Senior", "Lead", "Manager", "Director", "VP", "C-Level"] },
      { key: "remote", label: "Work Mode", type: "select", options: ["Remote", "Hybrid", "On-site"] },
    ],
    buildPrompt: (f) => `Write a compelling job description for a ${f.level || ""} ${f.title} at ${f.company || "our company"}. Work mode: ${f.remote || "flexible"}. Include engaging intro, key responsibilities, requirements (must-have vs nice-to-have), benefits, company culture snippet, and application instructions.`
  },

  // Legal & Finance
  {
    id: "contract-template", label: "Contract Template", icon: "⚖️", category: "Legal",
    description: "Professional contract with terms and clauses",
    fields: [
      { key: "type", label: "Contract Type", type: "select", options: ["Service Agreement", "NDA", "Employment Contract", "Partnership Agreement", "Consulting Agreement", "Freelancer Contract"], required: true },
      { key: "parties", label: "Parties Involved", type: "text", placeholder: "e.g., Company A and Contractor B" },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "e.g., Pakistan, Delaware USA" },
    ],
    buildPrompt: (f) => `Draft a professional ${f.type} between ${f.parties || "the parties"}. Jurisdiction: ${f.jurisdiction || "to be specified"}. Include definitions, scope of work/services, payment terms, confidentiality, intellectual property, termination clauses, dispute resolution, and signature blocks. Note: This is a template and should be reviewed by legal counsel.`
  },
  {
    id: "financial-report", label: "Financial Report", icon: "💰", category: "Finance",
    description: "P&L summary with cash flow analysis",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Company name", required: true },
      { key: "period", label: "Reporting Period", type: "select", options: ["Monthly", "Quarterly", "Annual", "YTD"] },
      { key: "metrics", label: "Key Metrics to Include", type: "textarea", placeholder: "e.g., Revenue, MRR, CAC, LTV, Burn rate" },
    ],
    buildPrompt: (f) => `Create a ${f.period || "quarterly"} financial analysis report for ${f.company}. Key metrics: ${f.metrics || "standard financial metrics"}. Include executive summary, P&L summary with trends, cash flow analysis, key ratios, variance analysis, and recommendations. Use tables for data presentation.`
  },
  {
    id: "investor-update", label: "Investor Update", icon: "📨", category: "Finance",
    description: "Monthly update with metrics and milestones",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Your startup name", required: true },
      { key: "month", label: "Month/Period", type: "text", placeholder: "e.g., May 2026" },
      { key: "highlights", label: "Key Highlights", type: "textarea", placeholder: "e.g., Launched v2.0, Closed $500K deal, Hired CTO" },
      { key: "metrics", label: "Key Metrics", type: "textarea", placeholder: "e.g., MRR: $50K, Users: 5000, Churn: 2%" },
    ],
    buildPrompt: (f) => `Write a ${f.month || "monthly"} investor update email for ${f.company}. Highlights: ${f.highlights || "recent achievements"}. Metrics: ${f.metrics || "key KPIs"}. Include TL;DR at top, metrics dashboard, wins & milestones, challenges & learnings, priorities for next month, and specific asks from investors.`
  },
  {
    id: "budget-plan", label: "Budget Plan", icon: "💵", category: "Finance",
    description: "Annual budget with department allocations",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Company name", required: true },
      { key: "year", label: "Budget Year", type: "text", placeholder: "e.g., 2027" },
      { key: "total", label: "Total Budget", type: "text", placeholder: "e.g., $1M, $5M" },
      { key: "departments", label: "Departments", type: "textarea", placeholder: "e.g., Engineering, Marketing, Sales, Operations" },
    ],
    buildPrompt: (f) => `Create a detailed ${f.year || "annual"} budget plan for ${f.company}. Total budget: ${f.total || "to be allocated"}. Departments: ${f.departments || "all departments"}. Include executive summary, department-wise allocation with justification, quarterly breakdown, contingency reserves, ROI projections, and approval workflow.`
  },
];

const TEMPLATE_CATEGORIES = ["All", "Strategy", "Marketing", "Operations", "Legal", "Finance"];

function CanvasDocument({ doc, onBack }) {
  const contentRef = useRef(null);

  const handleExportPDF = () => {
    try {
      const printWindow = window.open("", "_blank");
      if (!printWindow) { alert("Please allow popups to export PDF"); return; }
      printWindow.document.write(`
        <html><head><title>${doc.title}</title>
        <style>
          @page { margin: 0; size: A4; }
          @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
          * { box-sizing: border-box; }
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            padding: 48px;
            line-height: 1.75;
            color: #e5e7eb;
            background: linear-gradient(135deg, #0d1117 0%, #161b22 50%, #0d1117 100%);
            min-height: 100vh;
            max-width: 900px;
            margin: 0 auto;
          }
          .header-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(6, 182, 212, 0.15);
            border: 1px solid rgba(6, 182, 212, 0.3);
            padding: 6px 14px;
            border-radius: 20px;
            margin-bottom: 24px;
            font-size: 11px;
            font-weight: 700;
            color: #06b6d4;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .header-badge::before { content: '◉'; font-size: 10px; }
          h1 { font-size: 28px; font-weight: 800; color: #ffffff; margin: 32px 0 16px; border-bottom: 2px solid rgba(6, 182, 212, 0.4); padding-bottom: 12px; }
          h2 { font-size: 20px; font-weight: 700; color: #ffffff; margin: 28px 0 14px; padding-left: 12px; border-left: 4px solid #06b6d4; }
          h3 { font-size: 16px; font-weight: 600; color: #d1d5db; margin: 20px 0 10px; }
          p { color: #9ca3af; font-size: 14px; margin-bottom: 14px; }
          ul, ol { color: #9ca3af; font-size: 14px; padding-left: 24px; margin-bottom: 16px; }
          li { margin-bottom: 8px; }
          li::marker { color: #06b6d4; }
          strong, b { color: #ffffff; font-weight: 600; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; border-radius: 8px; overflow: hidden; }
          th { background: rgba(6, 182, 212, 0.2); border: 1px solid rgba(255,255,255,0.1); padding: 12px 16px; font-size: 12px; font-weight: 700; color: #06b6d4; text-align: left; text-transform: uppercase; letter-spacing: 0.5px; }
          td { background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255,255,255,0.08); padding: 12px 16px; font-size: 13px; color: #d1d5db; }
          tr:nth-child(even) td { background: rgba(255, 255, 255, 0.02); }
          blockquote { border-left: 3px solid #06b6d4; padding: 12px 16px; margin: 16px 0; background: rgba(6, 182, 212, 0.08); border-radius: 0 8px 8px 0; color: #d1d5db; font-style: italic; }
          code { background: rgba(6, 182, 212, 0.15); color: #06b6d4; padding: 3px 8px; border-radius: 4px; font-size: 13px; font-family: 'SF Mono', Consolas, monospace; }
          pre { background: rgba(0, 0, 0, 0.5); padding: 16px; border-radius: 8px; overflow-x: auto; border: 1px solid rgba(255,255,255,0.08); }
          pre code { background: none; padding: 0; color: #e5e7eb; }
          hr { border: none; height: 1px; background: linear-gradient(to right, transparent, rgba(6, 182, 212, 0.4), transparent); margin: 32px 0; }
          .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,0.1); text-align: center; font-size: 11px; color: #6b7280; }
          .footer::before { content: 'Generated by Kronos AI'; color: #06b6d4; }
        </style></head>
        <body>
          ${contentRef.current?.innerHTML || doc.content}
        </body></html>
      `);
      printWindow.document.close();
      setTimeout(() => { try { printWindow.print(); } catch(e) {} }, 500);
    } catch (e) {
      console.warn("PDF export error:", e);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(doc.content.replace(/<[^>]*>/g, ""));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Back to Fable
        </button>
        <div className="flex gap-2">
          <ActionButton onClick={handleCopy} label="Copy Text" />
          <ActionButton onClick={handleExportPDF} label="Export PDF" />
        </div>
      </div>

      {/* Document */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: "#000000",
          border: "1px solid rgba(6,182,212,0.15)"
        }}
      >
        {/* Title bar */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between"
          style={{ background: "rgba(0,0,0,0.5)" }}>
          <div>
            <h2 className="text-white font-bold text-lg">{doc.title}</h2>
            <p className="text-gray-500 text-xs mt-1">Generated with Claude AI · {new Date(doc.created_at).toLocaleDateString()}</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)" }}>
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#06b6d4" opacity="0.3"/><circle cx="12" cy="12" r="6" fill="#06b6d4"/></svg>
            <span className="text-[10px] font-semibold text-cyan-400">Fable AI</span>
          </div>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className="px-6 py-6 prose prose-invert prose-sm max-w-none
            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-6 [&_h1]:mb-3
            [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-5 [&_h2]:mb-2
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-200 [&_h3]:mt-4 [&_h3]:mb-2
            [&_p]:text-gray-300 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3
            [&_ul]:text-gray-300 [&_ul]:text-sm [&_ul]:space-y-1.5 [&_ul]:mb-4
            [&_ol]:text-gray-300 [&_ol]:text-sm [&_ol]:space-y-1.5 [&_ol]:mb-4
            [&_li]:text-gray-300
            [&_strong]:text-white [&_strong]:font-semibold
            [&_table]:w-full [&_table]:border-collapse [&_table]:my-4
            [&_th]:bg-white/5 [&_th]:border [&_th]:border-white/10 [&_th]:px-3 [&_th]:py-2 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-white [&_th]:text-left
            [&_td]:border [&_td]:border-white/10 [&_td]:px-3 [&_td]:py-2 [&_td]:text-xs [&_td]:text-gray-300
            [&_blockquote]:border-l-2 [&_blockquote]:border-cyan-500/50 [&_blockquote]:pl-4 [&_blockquote]:text-gray-400 [&_blockquote]:italic
            [&_code]:bg-white/5 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-cyan-400 [&_code]:text-xs
            [&_hr]:border-white/10 [&_hr]:my-6"
          dangerouslySetInnerHTML={{ __html: doc.content }}
        />
      </div>
    </div>
  );
}

// Action Button Component
function ActionButton({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-xl text-xs text-gray-300 transition-all duration-300"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.08)"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(6,182,212,0.3)";
        e.currentTarget.style.boxShadow = "0 0 15px rgba(6,182,212,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {label}
    </button>
  );
}

export default function CanvasTab() {
  const { subscription } = useSubscription();
  const [documents, setDocuments] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [fieldValues, setFieldValues] = useState({});
  const [scraping, setScraping] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("info");

  const handleVoiceTranscript = useCallback((text, isInterim) => {
    if (!isInterim) {
      setPrompt(prev => prev + text + " ");
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const { data } = await api.get("/canvas/documents");
      setDocuments(data.documents || []);
    } catch (e) {
      // Not logged in or no documents
    }
  };

  const handleGenerate = async () => {
    const finalPrompt = buildFinalPrompt();
    if (!finalPrompt.trim()) return;
    setGenerating(true);
    setError("");

    try {
      const { data } = await api.post("/canvas/generate", {
        prompt: finalPrompt.trim(),
        template: selectedTemplate,
      });

      if (data.error) {
        setError(data.error);
      } else {
        setActiveDoc(data);
        setPrompt("");
        setSelectedTemplate(null);
        loadDocuments();
      }
    } catch (e) {
      setError(e.response?.data?.error || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/canvas/documents/${docId}`);
      setDocuments(documents.filter(d => d.id !== docId));
      if (activeDoc?.id === docId) setActiveDoc(null);
    } catch (e) {}
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template.id);
    setFieldValues({});
  };

  const selectedTemplateObj = TEMPLATES.find(t => t.id === selectedTemplate);

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
  };

  const handleScrapeWebsite = async (url) => {
    if (!url || !url.startsWith("http")) return;
    setScraping(true);
    try {
      const { data } = await api.post("/scrape-website", { url });
      const info = data.business_info || data;
      if (info && (info.name || info.description || info.business_type)) {
        const filled = [];
        const updates = {};

        if (info.name) { updates.company = info.name; updates.your_company = info.name; filled.push(`Company: ${info.name}`); }
        if (info.business_type) { updates.industry = info.business_type; filled.push(`Industry: ${info.business_type}`); }
        if (info.description || info.about) {
          const desc = info.description || info.about;
          updates.description = desc;
          updates.your_expertise = desc.slice(0, 300);
          filled.push(`Description found`);
        }
        if (info.services?.length) {
          updates.services = info.services.join(", ");
          filled.push(`${info.services.length} services`);
        }
        if (info.phone) { updates.phone = info.phone; filled.push(`Phone: ${info.phone}`); }
        if (info.email) { updates.email = info.email; filled.push(`Email: ${info.email}`); }

        setFieldValues(prev => ({ ...prev, ...updates }));
        setMsg(filled.length > 0 ? `Analyzed! Found: ${filled.join(" | ")}` : "");
        setMsgType("success");
      } else {
        setMsg("Could not extract info from this website. Try a different URL.");
        setMsgType("error");
      }
    } catch (e) {
      setMsg("Failed to analyze website. Check the URL and try again.");
      setMsgType("error");
    } finally {
      setScraping(false);
    }
  };

  const buildFinalPrompt = () => {
    if (selectedTemplateObj?.buildPrompt && Object.keys(fieldValues).length > 0) {
      return selectedTemplateObj.buildPrompt(fieldValues);
    }
    return prompt;
  };

  // Viewing a document
  if (activeDoc) {
    return (
      <div className="space-y-6">
        <CanvasDocument doc={activeDoc} onBack={() => setActiveDoc(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-6 text-center"
        style={{
          background: "#000000",
          border: "1px solid rgba(6,182,212,0.15)"
        }}
      >
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

        <div className="flex items-center justify-center gap-2 mb-3">
          <svg className="w-8 h-8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="none" stroke="#06b6d4" strokeWidth="1.5"/><circle cx="12" cy="12" r="7" fill="none" stroke="#06b6d4" strokeWidth="1.5"/><circle cx="12" cy="12" r="3" fill="#06b6d4"/></svg>
          <h1 className="text-2xl font-black text-white">Fable AI</h1>
        </div>
        <p className="text-gray-400 text-sm max-w-lg mx-auto">
          Craft narratives, strategies, and documents with mythical precision — powered by <span className="text-cyan-400 font-semibold">Claude AI</span>
        </p>

        {/* Powered by badge */}
        <div className="flex justify-center mt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)" }}>
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#06b6d4" opacity="0.3"/><circle cx="12" cy="12" r="6" fill="#06b6d4"/></svg>
            <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Powered by Claude Sonnet 4.6</span>
          </div>
        </div>
      </div>

      {/* Input Section */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: "#000000",
          border: "1px solid rgba(255,255,255,0.06)"
        }}
      >
        <div className="px-5 py-4 border-b border-white/[0.06]" style={{ background: "rgba(0,0,0,0.5)" }}>
          <h3 className="text-white font-bold text-sm">Create New Document</h3>
        </div>

        <div className="p-5 space-y-4">
          {/* Template Categories */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Enterprise Templates</p>
              <div className="flex gap-1">
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all duration-300 ${
                      selectedCategory === cat
                        ? "text-cyan-400"
                        : "text-gray-500 hover:text-gray-300"
                    }`}
                    style={{
                      background: selectedCategory === cat ? "rgba(6,182,212,0.1)" : "rgba(255,255,255,0.02)",
                      border: selectedCategory === cat ? "1px solid rgba(6,182,212,0.3)" : "1px solid transparent"
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TEMPLATES.filter(t => selectedCategory === "All" || t.category === selectedCategory).map((t) => (
                <TemplateButton
                  key={t.id}
                  template={t}
                  selected={selectedTemplate === t.id}
                  onClick={() => handleTemplateClick(t)}
                />
              ))}
            </div>
          </div>

          {/* Dynamic Form Fields for Selected Template */}
          {selectedTemplateObj && selectedTemplateObj.fields && (
            <div
              className="mt-4 p-4 rounded-xl space-y-3"
              style={{
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(255,255,255,0.06)"
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                    <span>{selectedTemplateObj.icon}</span>
                    {selectedTemplateObj.label}
                  </h4>
                  <p className="text-gray-500 text-xs mt-0.5">{selectedTemplateObj.description}</p>
                </div>
                <button
                  onClick={() => { setSelectedTemplate(null); setFieldValues({}); }}
                  className="text-gray-500 hover:text-white p-1 transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {selectedTemplateObj.fields.map((field) => (
                  <div key={field.key} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                    <label className="block text-xs text-gray-400 mb-1 font-medium">
                      {field.label}
                      {field.required && <span className="text-cyan-400 ml-0.5">*</span>}
                    </label>
                    {field.type === "textarea" ? (
                      <textarea
                        value={fieldValues[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full h-20 px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 resize-none focus:outline-none transition-all"
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          border: "1px solid rgba(255,255,255,0.08)"
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = "rgba(6,182,212,0.4)"}
                        onBlur={(e) => e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"}
                      />
                    ) : field.type === "select" ? (
                      <select
                        value={fieldValues[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-white text-sm focus:outline-none transition-all"
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          border: "1px solid rgba(255,255,255,0.08)"
                        }}
                      >
                        <option value="">Select...</option>
                        {field.options?.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === "url" ? (
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={fieldValues[field.key] || ""}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="flex-1 px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none transition-all"
                          style={{
                            background: "rgba(0,0,0,0.5)",
                            border: "1px solid rgba(255,255,255,0.08)"
                          }}
                        />
                        <button
                          onClick={() => handleScrapeWebsite(fieldValues[field.key])}
                          disabled={scraping || !fieldValues[field.key]}
                          className="px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-40 transition-all flex items-center gap-1.5"
                          style={{
                            background: "rgba(6,182,212,0.1)",
                            border: "1px solid rgba(6,182,212,0.3)",
                            color: "#06b6d4"
                          }}
                        >
                          {scraping ? (
                            <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20"/></svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"/></svg>
                          )}
                          Analyze
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={fieldValues[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none transition-all"
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          border: "1px solid rgba(255,255,255,0.08)"
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              {msg && (
                <div className={`mt-3 px-3 py-2 rounded-xl text-xs font-medium ${
                  msgType === "success" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                  msgType === "error" ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                  "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                }`}>
                  {msg}
                </div>
              )}
            </div>
          )}

          {/* Prompt Input - with gradient glow outline */}
          <div
            className="relative rounded-2xl p-[1px] transition-all duration-500"
            style={{
              background: "linear-gradient(135deg, rgba(6,182,212,0.4), rgba(168,85,247,0.3), rgba(59,130,246,0.4))",
              boxShadow: "0 0 30px rgba(6,182,212,0.15), 0 0 60px rgba(168,85,247,0.08)"
            }}
          >
            <div className="relative rounded-2xl overflow-hidden" style={{ background: "#000000" }}>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleGenerate(); } }}
                placeholder={selectedTemplateObj ? "Additional context or instructions (optional)..." : "Describe what you want to create..."}
                className="w-full h-32 px-5 py-4 text-white text-sm placeholder:text-gray-500 resize-none focus:outline-none transition-all"
                style={{ background: "transparent" }}
              />
              <VoiceInput onTranscript={handleVoiceTranscript} className="absolute bottom-3 right-32" size="md" />
              <button
                onClick={handleGenerate}
                disabled={generating || (!prompt.trim() && !selectedTemplateObj)}
                className="absolute bottom-3 right-3 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-40 transition-all flex items-center gap-2"
                style={{
                  background: generating ? "rgba(6,182,212,0.2)" : "linear-gradient(135deg, rgba(6,182,212,0.4), rgba(168,85,247,0.3))",
                  border: "1px solid rgba(6,182,212,0.5)",
                  boxShadow: generating ? "none" : "0 0 20px rgba(6,182,212,0.25)"
                }}
              >
                {generating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20"/></svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Previous Documents */}
      {documents.length > 0 && (
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "#000000",
            border: "1px solid rgba(255,255,255,0.06)"
          }}
        >
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between"
            style={{ background: "rgba(0,0,0,0.5)" }}>
            <h3 className="text-white font-bold text-sm">Your Documents</h3>
            <span className="text-xs text-gray-500">{documents.length} documents</span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onClick={() => setActiveDoc(doc)}
                onDelete={() => handleDelete(doc.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Template Button Component
function TemplateButton({ template, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-start gap-2 rounded-xl text-sm transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: selected ? "rgba(6,182,212,0.08)" : "#000000",
        border: selected ? "1px solid rgba(6,182,212,0.4)" : "1px solid rgba(255,255,255,0.12)",
        boxShadow: selected ? "0 0 25px rgba(6,182,212,0.15)" : "none",
        padding: "16px 18px",
        minHeight: "72px"
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = "rgba(6,182,212,0.3)";
          e.currentTarget.style.boxShadow = "0 0 20px rgba(6,182,212,0.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      <div className="flex items-center gap-2.5">
        <span className="text-lg">{template.icon}</span>
        <span className={`font-semibold ${selected ? "text-cyan-400" : "text-gray-200"}`}>{template.label}</span>
      </div>
      <span className="text-xs text-gray-500">{template.category}</span>
    </button>
  );
}

// Document Row Component
function DocumentRow({ doc, onClick, onDelete }) {
  return (
    <div
      className="flex items-center justify-between px-5 py-3 transition-all duration-300 cursor-pointer"
      style={{ background: "transparent" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <button onClick={onClick} className="flex-1 text-left">
        <p className="text-sm text-white font-medium">{doc.title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{new Date(doc.created_at).toLocaleDateString()} · {doc.template || "Custom"}</p>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="text-gray-600 hover:text-red-400 transition-colors p-1"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/></svg>
      </button>
    </div>
  );
}
